// Build-time data bridge for static rendering.
//
// Under `web.output: "static"`, each dynamic route is rendered to HTML once, in
// Node, at export time. React effects do NOT run during that render, so the
// client data hooks (useEvent / useArticle) return null and their <Head> tags
// would be empty in the emitted HTML — invisible to no-JS social crawlers.
//
// `generateStaticParams` DOES run (and can await) before those renders, in the
// same process. So it fetches every record and stashes it here; the page reads
// it back synchronously during its build render and bakes real per-page meta
// (and body content) into the static HTML.
//
// In the browser the build-time cache starts empty. To keep the FIRST client
// render identical to the baked HTML — otherwise React throws a hydration
// mismatch (#418) — each page also bakes its record into the page as a JSON
// <script> (see <PrefetchState>). getPrefetched reads that back on the client so
// the first render uses the same record the server did; the client fetch then
// takes over with fresh data.

const buckets = new Map<string, Map<string, unknown>>();

export function setPrefetched<T>(bucket: string, key: string, value: T): void {
  let b = buckets.get(bucket);
  if (!b) {
    b = new Map();
    buckets.set(bucket, b);
  }
  b.set(key, value);
}

export function getPrefetched<T>(bucket: string, key: string | undefined): T | undefined {
  if (!key) return undefined;
  const cached = buckets.get(bucket)?.get(key);
  if (cached !== undefined) return cached as T;
  // Keep what the page baked: on a client-side navigation away, the head (and
  // its seed <script>s) is swapped for the next page's, so coming back with
  // the browser's Back button would otherwise find nothing — and a component
  // that reads its seed only once (the dubai home's category guide) would
  // render without its photos.
  const baked = readBakedState<T>(bucket, key);
  if (baked !== undefined) setPrefetched(bucket, key, baked);
  return baked;
}

// Stable DOM id for the JSON <script> a page bakes for one record.
export function prefetchElementId(bucket: string, key: string): string {
  return `__prefetch__${bucket}__${key}`;
}

// Serialize a record for embedding inside a <script>. React renders script text
// children verbatim (no HTML escaping), so escape `<` to keep a `</script>` in
// the data from ending the tag early; JSON.parse restores it transparently.
export function serializePrefetch(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

// Client-only: read the JSON a page baked into its static HTML. During the
// initial hydration render the baked <head> is still intact (react-helmet only
// reconciles the head in a post-render effect), so this returns synchronously.
function readBakedState<T>(bucket: string, key: string): T | undefined {
  if (typeof document === 'undefined') return undefined;
  const el = document.getElementById(prefetchElementId(bucket, key));
  if (!el?.textContent) return undefined;
  try {
    return JSON.parse(el.textContent) as T;
  } catch {
    return undefined;
  }
}
